import type { DocNode } from '@atlaskit/adf-schema';

import { REDACT_EXTENSION_KEY, REDACT_EXTENSION_TYPE } from './constants';

const REDACTED_PATTERN = /(\[REDACT(?:ED)?\])/g;

type AdfNode = {
	type: string;
	text?: string;
	marks?: Array<{ type: string; attrs?: Record<string, unknown> }>;
	attrs?: Record<string, unknown>;
	content?: AdfNode[];
};

function splitTextNode(node: AdfNode): AdfNode[] {
	const text = node.text ?? '';
	// Reset lastIndex for the global regex
	REDACTED_PATTERN.lastIndex = 0;
	const parts = text.split(REDACTED_PATTERN);

	return parts.flatMap((part): AdfNode[] => {
		if (!part) {
			return [];
		}
		if (/^\[REDACT(?:ED)?\]$/.test(part)) {
			return [
				{
					type: 'inlineExtension',
					attrs: {
						extensionType: REDACT_EXTENSION_TYPE,
						extensionKey: REDACT_EXTENSION_KEY,
						parameters: {},
						text: '',
					},
				},
			];
		}
		return [{ type: 'text', text: part, ...(node.marks ? { marks: node.marks } : {}) }];
	});
}

function walkNode(node: AdfNode): AdfNode {
	if (!node.content) {
		return node;
	}

	const newContent: AdfNode[] = [];
	for (const child of node.content) {
		if (child.type === 'text' && child.text && REDACTED_PATTERN.test(child.text)) {
			REDACTED_PATTERN.lastIndex = 0;
			newContent.push(...splitTextNode(child));
		} else {
			newContent.push(walkNode(child));
		}
	}

	return { ...node, content: newContent };
}

/**
 * ADF transformer that replaces [REDACT] / [REDACTED] text tokens with inline extension
 * nodes, which are rendered as scribble bars by RedactScribbleExtension.
 * Pass this as the `adfTransformer` prop to ContentRenderer.
 */
export function injectRedactScribbles(adf: DocNode): DocNode {
	return walkNode(adf as AdfNode) as DocNode;
}
